import { view } from "@risingstack/react-easy-state";
import React, { useEffect, useState } from 'react'
import GenericPageContainer from "../../GenericPageElements/GenericPageContainer";
import Select from 'react-select';
import DatePicker from "../date-picker/DatePicker";
import moment from 'moment';
import 'moment/locale/en-gb';
import request, { cancelAllRequests } from "../../helpers/request";
import endpoints from "../../helpers/endpoints";
import "./workflowReport.scss";
import { formatDate } from "../../helpers/formatDate";
import getWorkflowStatuses from "../../helpers/getWorkflowStatuses";

export default view(function WorkflowReport() {
  const tableHeaders = getWorkflowStatuses().map(outcome => outcome.label)
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [excelIsLoading, setExcelIsLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [expandedUsers, setExpandedUsers] = useState([])
  const [fromTime, setFromTime] = useState(moment().utc().locale('en-gb').subtract(7, 'days').startOf('day'));
  const [toTime, setToTime] = useState(moment().utc().locale('en-gb').startOf('day'));

  useEffect(() => {
    request(true).get(endpoints.WORKFLOW_REPORTS_USERS).then(r => {
        setUsers(r.data)
    })
  }, [])

  const getData = () => {
    setIsLoading(true)
    request(true).post(endpoints.WORKFLOW_REPORTS_DATA, {
      users: selectedUsers.map(selectedUser => selectedUser.value),
      fromTime,
      toTime
    },
    {doesCancel: true}
    ).then(r => {
      setData(r.data)
      setIsLoading(false)
    }).catch(error => {
      console.log(error)
      setIsLoading(false)
    })
  }

  const getExcelData = () => {
    setExcelIsLoading(true)
    request(true).post(endpoints.WORKFLOW_REPORTS_EXCEL_DATA, {
      users: selectedUsers.map(selectedUser => selectedUser.value),
      fromTime,
      toTime
    },
    {doesCancel: true}
    ).then(r => {
      const csv = [] 
      csv.push(['Core SPID', 'Agent', 'Status', 'Update Date']);
      const outcomeIndex = tableHeaders
        .reduce((m, outcome, i) => (m[outcome] = i, m), {});
      r.data.sort((a, b) => {
        if (a.assigned_to !== b.assigned_to) {
          return a.assigned_to - b.assigned_to;
        }
        return (outcomeIndex[a.WS_Outcome] ?? Infinity)
            - (outcomeIndex[b.WS_Outcome] ?? Infinity);
      });
      r.data.forEach(row => {
        csv.push([
          row.Core_SPID,
          users.find(user => user.value === parseInt(row.assigned_to))?.label ?? "Deleted User",
          row.WS_Outcome,
          row.WS_Outcome_Date
        ]);
      });

      const filename = 'address_portal_workflow_reports'
      const csvFile = new Blob([csv.join("\n")], { type: "text/csv" });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(csvFile, `${filename}.csv`);
      } else {
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(csvFile);
          link.setAttribute('href', url);
          link.setAttribute('download', `${filename}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      setExcelIsLoading(false)
    }).catch(error => {
      window.alert('Failed to download excel data')
      console.log(error)
      setExcelIsLoading(false)
    })
  }

  useEffect(() => {
    if (selectedUsers.length > 0) {
      getData()
    } else {
      cancelAllRequests()
      setData([])
      setExpandedUsers([])
    }
  }, [selectedUsers, fromTime, toTime])

  return (
      <GenericPageContainer
      containerWidth={"95vw"}
      className={'workflow-report'}
      title={'Workflow Reports'}
      titleIconClass={"fak fa-id-business-icon fa-2x colour-primary"}
    >
      <div className="grid grid-rows-2 grid-gap-15">
        <div className="flex" style={{ gap: '20px' }}>
          <div style={{ minWidth: '30em'}}>
            <p className="font-weight-600">Users</p>
            <Select
                isMulti
                isClearable
                value={selectedUsers}
                options={[{ label: 'All Users', value: '' }].concat(users)}
                onChange={(selected) => {
                  if (selected.some(option => option.value === "") && !selectedUsers.some(option => option.value === "")) {
                    setSelectedUsers([...selected.filter(option => option.value === '')])
                  } else {
                    setSelectedUsers([...selected.filter(option => option.value !== '')])
                  }
                }}
                styles={{
                    container: (baseStyles, state) => ({
                        ...baseStyles,
                        display: 'grid'
                    }),
                }}
            />
          </div>
          <div>
            <p className="font-weight-600">From</p>
            <DatePicker time={fromTime} disableFuture={true} onChange={date => setFromTime(date)} />
          </div>
          <div>
            <p className="font-weight-600">To</p>
            <DatePicker time={toTime} disableFuture={true} onChange={date => setToTime(date)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', height: 'min-content', alignSelf: 'end' }}>
            { excelIsLoading &&
              <i style={{ marginRight: '1em' }} className="fa fa-spinner fa-spin"></i>
            }
            <img style={{ cursor: 'pointer', width: '2em' }} src='/Excel-logo.svg' onClick={ () => selectedUsers.length > 0 && !excelIsLoading && getExcelData()} />
          </div>
        </div>
        <div>
          <table className="table borders">
            <thead>
              <tr>
                <th style={{ width: '20ch' }}>User</th>
                {
                  tableHeaders.map(header => <th>{header}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {
                isLoading ?
                  <tr>
                    <td colSpan={'100%'}>
                      <i className="fa fa-spinner fa-spin"></i>
                    </td>
                  </tr>
                :
                  Object.keys(data).length > 0 ?
                    Object.keys(data).map(userKey => {
                      const user = users.find(user => user.value === parseInt(userKey));
                      const userData = data[userKey]
                      const isExpanded = expandedUsers.includes(user.value)
                      return (
                        <>
                          <tr className="font-weight-600">
                            <td className="whitespace-nowrap" style={{ borderBottom: '1px solid black' }} onClick={() => setExpandedUsers(isExpanded ? [...expandedUsers.filter(expandedUser => expandedUser !== user.value)] : [...expandedUsers, user.value])}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ flex: '1', textAlign: 'center' }}>{user.label}</span>
                                {/* <i className="fa-solid fa-caret-right" style={{ transform: `rotate(${isExpanded ? "90deg" : "0deg"})`, transition: 'transform 0.3s ease' }}></i> */}
                              </div>
                            </td>
                            {
                              tableHeaders.map(header => {
                                return (
                                  <td style={{ borderBottom: '1px solid black' }}>{userData[header] ?? 0}</td>
                                )
                              })
                            }
                          </tr>
                          {/* {
                            modules.filter(module => module.value !== "").map(module => {
                              return (
                                <tr>
                                  <td className={`expandable-row ${isExpanded ? "expanded" : 'reduced'}`}><div>{module.label}</div></td>
                                  {
                                    tableHeaders.map(header => {
                                      return (
                                        <td className={`expandable-row ${isExpanded ? "expanded" : 'reduced'}`}><div>{userData[module.value]?.find(datum => datum.WS_Outcome === header)?.count ?? 0}</div></td>
                                      )
                                    })
                                  }
                                </tr>     
                              )
                            })
                          } */}
                        </>
                      )
                    })
                  :
                    <tr>
                      {new Array(tableHeaders.length + 1).fill('').map(header => <td></td>)}
                    </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </GenericPageContainer>
  )
})
